(() => {
    'use strict';

    const RUNTIME_ATTRIBUTE =
        'data-back-to-top-runtime-ready';

    if (
        document.documentElement.hasAttribute(
            RUNTIME_ATTRIBUTE
        )
    ) {
        return;
    }

    document.documentElement.setAttribute(
        RUNTIME_ATTRIBUTE,
        ''
    );

    class BackToTop {
        constructor(root = document) {
            this.button =
                root.querySelector(
                    '[data-back-to-top]'
                );

            if (!this.button) {
                return;
            }

            this.visible =
                false;

            this.ticking =
                false;

            this.abortController =
                new AbortController();

            this.handleScroll =
                this.handleScroll.bind(this);

            this.handleResize =
                this.handleResize.bind(this);

            this.handleClick =
                this.handleClick.bind(this);

            this.init();
        }

        init() {
            const signal =
                this.abortController.signal;

            this.button.hidden =
                false;

            this.button.setAttribute(
                'aria-hidden',
                'true'
            );

            this.button.setAttribute(
                'tabindex',
                '-1'
            );

            window.addEventListener(
                'scroll',
                this.handleScroll,
                {
                    passive: true,
                    signal,
                }
            );

            window.addEventListener(
                'resize',
                this.handleResize,
                {
                    passive: true,
                    signal,
                }
            );

            this.button.addEventListener(
                'click',
                this.handleClick,
                {
                    signal,
                }
            );

            this.update();
        }

        handleScroll() {
            this.requestUpdate();
        }

        handleResize() {
            this.requestUpdate();
        }

        requestUpdate() {
            if (this.ticking) {
                return;
            }

            this.ticking =
                true;

            window.requestAnimationFrame(
                () => {
                    this.update();

                    this.ticking =
                        false;
                }
            );
        }

        update() {
            if (!this.button) {
                return;
            }

            const scrollingElement =
                document.scrollingElement ||
                document.documentElement;

            const scrollTop =
                window.scrollY ||
                scrollingElement.scrollTop ||
                0;

            const scrollableDistance =
                Math.max(
                    0,
                    scrollingElement.scrollHeight -
                    window.innerHeight
                );

            const threshold =
                Math.max(
                    480,
                    window.innerHeight * 0.65
                );

            const shouldShow =
                scrollTop > threshold &&
                scrollableDistance > threshold;

            this.setVisibility(
                shouldShow
            );
        }

        setVisibility(shouldShow) {
            if (
                shouldShow ===
                this.visible
            ) {
                return;
            }

            this.visible =
                shouldShow;

            this.button.classList.toggle(
                'is-visible',
                shouldShow
            );

            this.button.setAttribute(
                'aria-hidden',
                String(!shouldShow)
            );

            if (shouldShow) {
                this.button.removeAttribute(
                    'tabindex'
                );
            } else {
                this.button.setAttribute(
                    'tabindex',
                    '-1'
                );
            }
        }

        handleClick() {
            const reducedMotion =
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches;

            window.scrollTo({
                top: 0,

                behavior:
                    reducedMotion
                        ? 'auto'
                        : 'smooth',
            });
        }

        destroy() {
            this.abortController.abort();

            this.button
                ?.classList.remove(
                    'is-visible'
                );

            if (this.button) {
                this.button.setAttribute(
                    'aria-hidden',
                    'true'
                );

                this.button.setAttribute(
                    'tabindex',
                    '-1'
                );
            }
        }
    }

    const initialize = () => {
        const existing =
            document.querySelector(
                '[data-back-to-top]'
            );

        if (!existing) {
            return;
        }

        new BackToTop(
            document
        );
    };

    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            initialize,
            {
                once: true,
            }
        );
    } else {
        initialize();
    }
})();